import {
  useHelpFeedbackSaveComment,
  useHelpFeedbackSaveRating,
} from "@/queries/use-help-feedback.query.ts";
import { HelpFeedbackRating } from "@/types/help-feedback.types.ts";
import { Box, Button, Stack, Text, Textarea, Title } from "@skailer-com/uikit";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

interface HelpArticleFeedbackProps {
  articleId: string;
  language: string;
}

const feedbackOptions = [
  {
    emoji: "👎",
    rating: HelpFeedbackRating.dislike,
    labelKey: "common:help_feedback_rating_dislike",
  },
  {
    emoji: "😐",
    rating: HelpFeedbackRating.neutral,
    labelKey: "common:help_feedback_rating_neutral",
  },
  {
    emoji: "👍",
    rating: HelpFeedbackRating.like,
    labelKey: "common:help_feedback_rating_like",
  },
];

export const HelpArticleFeedback = ({ articleId, language }: HelpArticleFeedbackProps) => {
  const { t } = useTranslation();
  const saveRating = useHelpFeedbackSaveRating();
  const saveComment = useHelpFeedbackSaveComment();

  const [selectedRating, setSelectedRating] = useState<HelpFeedbackRating | null>(null);
  const [comment, setComment] = useState("");
  const [pendingRating, setPendingRating] = useState<HelpFeedbackRating | null>(null);
  const [isCommentSubmitted, setIsCommentSubmitted] = useState(false);

  const promptText = useMemo(() => {
    if (selectedRating === HelpFeedbackRating.dislike) {
      return t("common:help_feedback_comment_dislike_prompt");
    }

    if (selectedRating === HelpFeedbackRating.like) {
      return t("common:help_feedback_comment_like_prompt");
    }

    return t("common:help_feedback_comment_neutral_prompt");
  }, [selectedRating, t]);

  const handleSelectRating = async (rating: HelpFeedbackRating) => {
    setPendingRating(rating);

    try {
      await saveRating.mutateAsync({
        article_id: articleId,
        rating,
        language,
      });

      setSelectedRating(rating);
      setIsCommentSubmitted(false);

      setComment("");
    } finally {
      setPendingRating(null);
    }
  };

  const handleSubmitComment = async () => {
    const trimmedComment = comment.trim();

    if (!trimmedComment) {
      return;
    }

    await saveComment.mutateAsync({
      article_id: articleId,
      comment: trimmedComment,
      language,
    });

    setComment(trimmedComment);
    setIsCommentSubmitted(true);
  };

  const showThankYou = isCommentSubmitted;

  return (
    <Box className={"mt-10 border-t border-dark/5 "}>
      <Stack gap={"xl"}>
        <Title
          header={t("common:help_feedback_title")}
          caption={t("common:help_feedback_caption")}
          size={"md"}
          paddingTop={true}
          paddingBottom={true}
        />

        <Stack gap={"md"}>
          <Stack direction={"horizontal"} className="justify-evenly" gap="xs">
            {feedbackOptions.map((option) => {
              const isSelected = selectedRating === option.rating;
              const isLoading = pendingRating === option.rating && saveRating.isPending;

              return (
                <Stack key={option.rating} className="grow" gap={"xs"}>
                  <Button
                    type={"button"}
                    size={"lg"}
                    variant={isSelected ? "primary" : "secondary"}
                    isLoading={isLoading}
                    disabled={saveRating.isPending || saveComment.isPending}
                    onClick={() => {
                      void handleSelectRating(option.rating);
                    }}
                    className={"w-full min-h-20"}
                    aria-label={t(option.labelKey)}
                    title={t(option.labelKey)}
                  >
                    {!isLoading && <Text className={"text-3xl leading-none"}>{option.emoji}</Text>}
                  </Button>
                  <Text className={"self-center text-center"}>{t(option.labelKey)}</Text>
                </Stack>
              );
            })}
          </Stack>
        </Stack>

        {selectedRating !== null && !isCommentSubmitted && (
          <Stack>
            <Title header={promptText} size={"md"} paddingTop={true} paddingBottom={false} />
            <Box>
              <Textarea
                label={t("surveys:add_comment_to_your_answer") + "..."}
                placeholder={t("common:help_feedback_comment_placeholder")}
                value={comment}
                onChange={(event) => {
                  setComment(event.target.value);
                }}
              />
            </Box>
            <Stack direction={"horizontal"} className={"pt-6"} justify={"end"}>
              <Button
                type={"button"}
                size={"lg"}
                onClick={() => {
                  void handleSubmitComment();
                }}
                isLoading={saveComment.isPending}
                disabled={!comment.trim() || saveComment.isPending || saveRating.isPending}
              >
                {t("common:submit")}
              </Button>
            </Stack>
          </Stack>
        )}

        {showThankYou && (
          <Title
            header={t("common:help_feedback_thank_you_title")}
            caption={t("common:help_feedback_thank_you_text")}
            size={"md"}
          />
        )}
      </Stack>
    </Box>
  );
};
